/**
 * Card grids shared by the English and Bengali About pages. Wording comes from
 * src/content/about-content.ts and src/content/bn/about-content.ts. The
 * English page nests card headings as h3; the Bengali page uses h2, so each
 * grid takes a headingLevel.
 */
import { Check, Facebook, Linkedin, Mail, Phone } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SITE } from "@/content/site";

type Level = "h2" | "h3";
type Card = { t: string; d: string };

/** "Four pillars" cards. */
export function PillarGrid({ items, headingLevel }: { items: readonly Card[]; headingLevel: Level }) {
  const Heading = headingLevel;
  return (
    <div className="grid md:grid-cols-2 gap-6">
      {items.map((p, i) => (
        <Reveal key={p.t} delay={i * 0.05}>
          <div className="rounded-3xl border border-border p-10 h-full bg-card">
            <Heading className="font-display text-2xl">{p.t}</Heading>
            <p className="mt-4 text-muted-foreground">{p.d}</p>
          </div>
        </Reveal>
      ))}
    </div>
  );
}

/** Ticked reasons beside the "why choose" intro paragraph. */
export function WhyChecklist({ items }: { items: readonly string[] }) {
  return (
    <div className="grid gap-3">
      {items.map((w, i) => (
        <Reveal key={i} delay={i * 0.04}>
          <div className="flex gap-3 rounded-2xl border border-border p-5 bg-card">
            <span className="mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
              <Check size={14} />
            </span>
            <p className="text-sm text-muted-foreground leading-relaxed">{w}</p>
          </div>
        </Reveal>
      ))}
    </div>
  );
}

/** Focus / location facts with email, phone and social links. */
export function AboutContactCard({ facts }: { facts: readonly { label: string; value: string }[] }) {
  return (
    <div className="rounded-3xl border border-border p-10 bg-card">
      <div className="grid grid-cols-2 gap-6">
        {facts.map((f) => (
          <div key={f.label}>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{f.label}</div>
            <div className="mt-2 font-display text-2xl">{f.value}</div>
          </div>
        ))}
      </div>
      <div className="mt-8 space-y-3 text-sm">
        <a href={`mailto:${SITE.email}`} className="flex items-center gap-3 hover:text-primary">
          <Mail size={16} /> {SITE.email}
        </a>
        <a href={`tel:${SITE.phone}`} className="flex items-center gap-3 hover:text-primary">
          <Phone size={16} /> {SITE.phone}
        </a>
      </div>
      <div className="mt-8 flex gap-3">
        <a
          href={SITE.social.facebook}
          aria-label="Facebook"
          className="size-10 grid place-items-center rounded-full border border-border hover:bg-primary hover:text-primary-foreground"
        >
          <Facebook size={16} />
        </a>
        <a
          href={SITE.social.linkedin}
          aria-label="LinkedIn"
          className="size-10 grid place-items-center rounded-full border border-border hover:bg-primary hover:text-primary-foreground"
        >
          <Linkedin size={16} />
        </a>
      </div>
    </div>
  );
}

/** Skill names listed in a card. */
export function SkillsList({ skills }: { skills: readonly { t: string }[] }) {
  return (
    <div className="space-y-6 rounded-3xl border border-border p-10 bg-card">
      {skills.map((s) => (
        <div key={s.t}>
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">{s.t}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Numbered service tiles. */
export function AboutServiceGrid({
  services,
  headingLevel,
}: {
  services: readonly { i: string; t: string; d: string }[];
  headingLevel: Level;
}) {
  const Heading = headingLevel;
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-border rounded-3xl overflow-hidden border border-border">
      {services.map((s) => (
        <div key={s.i} className="bg-background p-8">
          <div className="font-display text-2xl text-gradient">{s.i}</div>
          <Heading className="mt-4 font-display text-xl">{s.t}</Heading>
          <p className="mt-3 text-sm text-muted-foreground">{s.d}</p>
        </div>
      ))}
    </div>
  );
}

/** Three "my approach" cards. */
export function ApproachCards({ items, headingLevel }: { items: readonly Card[]; headingLevel: Level }) {
  const Heading = headingLevel;
  return (
    <div className="grid md:grid-cols-3 gap-6">
      {items.map((c, i) => (
        <Reveal key={c.t} delay={i * 0.05}>
          <div className="rounded-3xl border border-border p-8 bg-card h-full">
            <Heading className="font-display text-xl">{c.t}</Heading>
            <p className="mt-3 text-sm text-muted-foreground">{c.d}</p>
          </div>
        </Reveal>
      ))}
    </div>
  );
}
